// Lightweight one-click PDF export helpers (no browser print dialog).
// Uses html2canvas + jsPDF, lazy-loaded so they don't bloat the initial bundle.

async function loadLibs() {
  const [{ default: html2canvas }, jsPDFModule] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);
  const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
  return { html2canvas, jsPDF };
}

// Load an image URL into a PNG dataURL (same-origin recommended to avoid taint).
function loadImage(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth || img.width;
          c.height = img.naturalHeight || img.height;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve({ dataUrl: c.toDataURL("image/png"), w: c.width, h: c.height });
        } catch (e) {
          resolve(null); // tainted canvas
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    } catch (e) {
      resolve(null);
    }
  });
}

// Draw centered logo + heading lines at the top of the current page.
// Returns the Y coordinate (pt) just below the header.
function drawHeader(pdf, { logo, headingLines = [], pageW, startY }) {
  let y = startY;
  if (logo && logo.dataUrl) {
    const logoH = 46;
    const ratio = logo.w && logo.h ? logo.w / logo.h : 1;
    const logoW = logoH * ratio;
    pdf.addImage(logo.dataUrl, "PNG", (pageW - logoW) / 2, y, logoW, logoH);
    y += logoH + 10;
  }
  const styles = [
    { size: 14, bold: true },
    { size: 10, bold: false },
    { size: 12, bold: true },
    { size: 10, bold: false },
  ];
  headingLines.filter(Boolean).forEach((line, i) => {
    const s = styles[Math.min(i, styles.length - 1)];
    pdf.setFont("helvetica", s.bold ? "bold" : "normal");
    pdf.setFontSize(s.size);
    pdf.text(String(line), pageW / 2, y, { align: "center" });
    y += s.size + 6;
  });
  return y + 6;
}

/**
 * Capture a DOM node and download it as a SINGLE-page PDF that fits the page.
 * Great for wide diagrams (e.g. the org chart) — renders on one landscape sheet.
 */
export async function exportNodeToPdf({
  node,
  filename = "dokumen.pdf",
  headingLines = [],
  logoUrl = null,
  orientation = "landscape",
}) {
  if (!node) throw new Error("Elemen tidak ditemukan");
  const { html2canvas, jsPDF } = await loadLibs();
  const logo = await loadImage(logoUrl);

  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: node.scrollWidth,
    windowHeight: node.scrollHeight,
  });

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 32;

  const headerBottom = drawHeader(pdf, {
    logo,
    headingLines,
    pageW,
    startY: margin,
  });

  const availW = pageW - margin * 2;
  const availH = pageH - headerBottom - margin;
  const ratio = Math.min(availW / canvas.width, availH / canvas.height);
  const imgW = canvas.width * ratio;
  const imgH = canvas.height * ratio;
  const x = (pageW - imgW) / 2;
  pdf.addImage(imgData, "PNG", x, headerBottom, imgW, imgH);
  pdf.save(filename);
}

/**
 * Capture a tall content node and download it as a MULTI-page PDF (portrait),
 * fitting the width and slicing across pages. Used for report pages.
 */
export async function exportReportToPdf({
  node,
  filename = "laporan.pdf",
  headingLines = [],
  logoUrl = null,
  avoidBreakSelector = null,
}) {
  if (!node) throw new Error("Elemen tidak ditemukan");
  const { html2canvas, jsPDF } = await loadLibs();
  const logo = await loadImage(logoUrl);

  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: node.scrollWidth,
    windowHeight: node.scrollHeight,
  });

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 32;
  const imgWidth = pageW - margin * 2;
  // canvas px per PDF point (when image drawn at imgWidth)
  const pxPerPt = canvas.width / imgWidth;

  const firstHeaderBottom = drawHeader(pdf, {
    logo,
    headingLines,
    pageW,
    startY: margin,
  });

  // Vertical boundaries (in canvas px) of elements that should not be split
  // across a page break, so slices end just before them when possible.
  let avoidZones = [];
  if (avoidBreakSelector) {
    try {
      const nodeRect = node.getBoundingClientRect();
      const factor = nodeRect.height ? canvas.height / nodeRect.height : 1;
      node.querySelectorAll(avoidBreakSelector).forEach((el) => {
        const r = el.getBoundingClientRect();
        const top = Math.max(0, (r.top - nodeRect.top) * factor);
        const bottom = (r.bottom - nodeRect.top) * factor;
        if (bottom > top) avoidZones.push({ top, bottom });
      });
      avoidZones.sort((a, b) => a.top - b.top);
    } catch (e) {
      avoidZones = [];
    }
  }

  let renderedPx = 0;
  let pageIndex = 0;
  while (renderedPx < canvas.height) {
    const topY = pageIndex === 0 ? firstHeaderBottom : margin;
    const availPt = pageH - topY - margin;
    const availPx = Math.floor(availPt * pxPerPt);
    let slicePx = Math.min(availPx, canvas.height - renderedPx);
    if (slicePx <= 0) break;

    // Avoid cutting through a keep-together block: end the slice just before it
    // (only when the block fits on a fresh page and this isn't the final slice).
    if (renderedPx + slicePx < canvas.height) {
      const proposedEnd = renderedPx + slicePx;
      for (const z of avoidZones) {
        if (
          z.top > renderedPx + 2 &&
          z.top < proposedEnd &&
          z.bottom > proposedEnd &&
          z.bottom - z.top <= availPx
        ) {
          const candidate = Math.floor(z.top - renderedPx);
          if (candidate > 0) slicePx = Math.min(slicePx, candidate);
        }
      }
    }

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = slicePx;
    sliceCanvas
      .getContext("2d")
      .drawImage(
        canvas,
        0,
        renderedPx,
        canvas.width,
        slicePx,
        0,
        0,
        canvas.width,
        slicePx
      );
    const sliceData = sliceCanvas.toDataURL("image/png");
    const sliceHeightPt = slicePx / pxPerPt;

    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceData, "PNG", margin, topY, imgWidth, sliceHeightPt);

    renderedPx += slicePx;
    pageIndex++;
  }

  pdf.save(filename);
}
